import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { getSubjectIcon } from "@/components/content/SubjectIcon";
import { useChapters, useSubjects } from "@/hooks/useCurriculum";
import { useDashboard } from "@/hooks/useProgress";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/cn";
import type { UserProgress } from "@/lib/api-types";
import { useMemo } from "react";

/**
 * Horizontal scroll of the user's last-touched chapters with a
 * completion ring + last accessed. Tap to jump back into the picker for
 * that chapter.
 *
 * Hidden when there are no recent chapters -- the resume card carries
 * the "get started" CTA in that case.
 */
export function RecentChapters({ classId }: { classId: string | null }) {
  const dashboard = useDashboard();
  const recent = useMemo(() => dashboard.data?.recent_chapters ?? [], [dashboard.data]);
  // Fall back to the class of the first recent chapter when the user has
  // no class_id set on their profile yet (we temporarily disabled the
  // onboarding gate -- see MANUAL_ACTIONS_PENDING #7). Without this,
  // useSubjects never fires and every subject label sticks on "Loading…".
  const effectiveClassId = classId ?? recent[0]?.class_id ?? null;
  const subjects = useSubjects(effectiveClassId ?? undefined);

  const ordered = useMemo(
    () =>
      recent
        .slice()
        .sort((a, b) => new Date(b.last_accessed_at).getTime() - new Date(a.last_accessed_at).getTime())
        .slice(0, 6),
    [recent],
  );

  if (ordered.length === 0) return null;

  return (
    <section>
      <header className="flex items-baseline justify-between mb-3">
        <h3 className="font-display text-sm font-semibold tracking-tight text-text-muted uppercase tracking-[0.14em]">
          Recent chapters
        </h3>
        <Link to="/subjects" className="text-xs text-text-muted hover:text-text">
          See all
        </Link>
      </header>

      <div
        className={cn(
          "flex gap-3",
          // Mobile + tablet: horizontal scroll with snap. Bleed past the
          // Container padding to the screen edge using matching breakpoint
          // pairs, so the first card's left edge aligns exactly with the
          // dashboard cards above.
          "overflow-x-auto pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6",
          "[scroll-snap-type:x_mandatory] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          // Desktop right-rail (lg+): stack vertically, full-width within
          // the column. The fixed w-[260px] cards don't fit cleanly in
          // a ~400px column slot, so a vertical stack matches the rhythm
          // of Subjects + AI Tutor below.
          "lg:flex-col lg:overflow-visible lg:m-0 lg:p-0 lg:pb-0",
        )}
      >
        {ordered.map((p, i) => (
          <RecentCard
            key={p.chapter_id}
            progress={p}
            subjectName={subjects.data?.find((s) => s.subject_id === p.subject_id)?.subject_name ?? "Subject"}
            delayIndex={i}
          />
        ))}
      </div>
    </section>
  );
}

function RecentCard({
  progress,
  subjectName,
  delayIndex,
}: {
  progress: UserProgress;
  subjectName: string;
  delayIndex: number;
}) {
  const chaptersQ = useChapters(progress.subject_id);
  const chapterName =
    chaptersQ.data?.find((c) => c.chapter_id === progress.chapter_id)?.chapter_name ?? "Chapter";
  const Icon = getSubjectIcon(subjectName);
  const rawPct = progress.chapter_completion_percent;
  const percent = Number.isFinite(rawPct)
    ? Math.min(100, Math.max(0, Math.round(rawPct)))
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.28, delay: delayIndex * 0.04, ease: [0.16, 1, 0.3, 1] }}
      className="[scroll-snap-align:start] flex-shrink-0 lg:flex-shrink lg:w-full"
    >
      <Link
        to={`/study/${encodeURIComponent(progress.chapter_id)}`}
        className={cn(
          // Mobile/tablet: peek-pattern card width. Desktop: fills column.
          "block w-[78vw] sm:w-[260px] lg:w-full",
          "rounded-2xl border border-border bg-surface p-4 sm:p-5",
          "hover:border-border-strong hover:shadow-[0_4px_12px_rgb(15_23_42_/_0.06)] hover:-translate-y-px",
          "transition-[border-color,box-shadow,transform] duration-200 ease-out",
        )}
      >
        <div className="flex items-start gap-3">
          <ProgressRing percent={percent} />
          <div className="flex-1 min-w-0">
            <p className="flex items-center gap-1.5 text-[11px] text-text-muted">
              <Icon className="h-3 w-3" />
              <span className="truncate">{subjectName}</span>
            </p>
            <h4 className="mt-1 font-medium text-[15px] tracking-tight truncate">
              {chapterName}
            </h4>
            <p className="mt-0.5 text-[11px] text-text-subtle">
              {formatRelative(progress.last_accessed_at)}
            </p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[11px] text-text-muted">
            {progress.modes_used?.length ?? 0} mode{progress.modes_used?.length === 1 ? "" : "s"} touched
          </span>
          <ChevronRight className="h-4 w-4 text-text-subtle" />
        </div>
      </Link>
    </motion.div>
  );
}

function ProgressRing({ percent }: { percent: number }) {
  const R = 14;
  const C = 2 * Math.PI * R;
  const offset = C - (percent / 100) * C;
  return (
    <div className="relative grid place-items-center h-10 w-10 flex-shrink-0">
      <svg className="absolute inset-0" viewBox="0 0 36 36">
        <circle cx="18" cy="18" r={R} fill="none" stroke="rgb(var(--surface-3))" strokeWidth="3" />
        <motion.circle
          cx="18" cy="18" r={R}
          fill="none"
          stroke="rgb(var(--brand))"
          strokeWidth="3"
          strokeLinecap="round"
          transform="rotate(-90 18 18)"
          initial={{ strokeDasharray: C, strokeDashoffset: C }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
        />
      </svg>
      <span className="text-[10px] font-semibold tabular-nums text-text">{percent}</span>
    </div>
  );
}
